import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import LinkIcon from "@mui/icons-material/Link";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import * as React from "react";
import SafeIframe from "../../components/SafeIframe";
import { useApi } from "../../containers/ApiContainer";
import { isValidYouTubeIframe } from "../../utils/validation";
import { ButtonUploadImage } from "../../widgets/ButtonUploadImage/ButtonUploadImage";

// Lazy load date picker field wrapper
const DatePickerField = React.lazy(() =>
  Promise.all([
    import("@mui/x-date-pickers/DateTimePicker"),
    import("@mui/x-date-pickers/LocalizationProvider"),
    import("@mui/x-date-pickers/AdapterDateFns"),
  ]).then(([dateTimePicker, localizationProvider, adapterDateFns]) => ({
    default: ({
      label,
      value,
      onChange,
      error,
      helperText,
    }: {
      label: string;
      value: Date | null;
      onChange: (value: Date | null) => void;
      error?: boolean;
      helperText?: string;
    }) => (
      <localizationProvider.LocalizationProvider
        dateAdapter={adapterDateFns.AdapterDateFns}
      >
        <dateTimePicker.DateTimePicker
          label={label}
          value={value}
          onChange={onChange}
          slotProps={{
            textField: {
              error,
              helperText,
              fullWidth: true,
            },
          }}
        />
      </localizationProvider.LocalizationProvider>
    ),
  }))
);

interface Announcement {
  id: string;
  title: string;
  body: string;
  video_url?: string;
  image_url?: string;
  action_link?: string;
  action_link_text?: string;
  expiration_date: string;
  created_at: string;
  updated_at: string;
  is_active?: boolean;
}

type MediaType = "none" | "video" | "image";

interface FormData {
  title: string;
  body: string;
  media_type: MediaType;
  video_url: string;
  image_url: string;
  action_link: string;
  action_link_text: string;
  expiration_date: Date | null;
}

const initialFormData: FormData = {
  title: "",
  body: "",
  media_type: "none",
  video_url: "",
  image_url: "",
  action_link: "",
  action_link_text: "",
  expiration_date: null,
};

export const AnnouncementsAdmin: React.FC = () => {
  const { postgrest } = useApi();
  const [announcements, setAnnouncements] = React.useState<Announcement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState<FormData>(initialFormData);
  const [errors, setErrors] = React.useState<Partial<FormData>>({});
  const [previewAnnouncement, setPreviewAnnouncement] =
    React.useState<Announcement | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const { data, error } = await postgrest
        .from("announcements")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Add is_active computed field
      const announcementsWithStatus = (data || []).map((announcement) => ({
        ...announcement,
        is_active: new Date(announcement.expiration_date) > new Date(),
      }));

      setAnnouncements(announcementsWithStatus);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.body.trim()) {
      newErrors.body = "Body is required";
    }

    if (!formData.expiration_date) {
      newErrors.expiration_date = "Expiration date is required";
    }

    // Validate media based on type
    if (
      formData.media_type === "video" &&
      formData.video_url &&
      !isValidYouTubeIframe(formData.video_url)
    ) {
      newErrors.video_url = "Please enter a valid YouTube iframe embed code";
    }

    if (formData.media_type === "video" && !formData.video_url.trim()) {
      newErrors.video_url = "Video URL is required when video is selected";
    }

    if (formData.media_type === "image" && !formData.image_url) {
      newErrors.image_url = "Please upload an image";
    }

    // Validate action link URL format if provided
    if (formData.action_link && formData.action_link.trim()) {
      try {
        new URL(formData.action_link);
      } catch {
        newErrors.action_link =
          "Please enter a valid URL (e.g., https://example.com)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setUploading(true);

      const announcementData: any = {
        title: formData.title,
        body: formData.body,
        video_url: formData.media_type === "video" ? formData.video_url : null,
        image_url: formData.media_type === "image" ? formData.image_url : null,
        action_link: formData.action_link || null,
        action_link_text: formData.action_link_text || null,
        expiration_date: formData.expiration_date?.toISOString(),
      };

      if (editingId) {
        const { error } = await postgrest
          .from("announcements")
          .update(announcementData)
          .eq("id", editingId);

        if (error) throw error;
      } else {
        const { error } = await postgrest
          .from("announcements")
          .insert(announcementData);

        if (error) throw error;
      }

      handleClose();
      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to save announcement:", error);
      alert("Failed to save announcement. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    // Determine media type based on what's present
    let mediaType: MediaType = "none";
    if (announcement.video_url) mediaType = "video";
    else if (announcement.image_url) mediaType = "image";

    setFormData({
      title: announcement.title,
      body: announcement.body,
      media_type: mediaType,
      video_url: announcement.video_url || "",
      image_url: announcement.image_url || "",
      action_link: announcement.action_link || "",
      action_link_text: announcement.action_link_text || "",
      expiration_date: new Date(announcement.expiration_date),
    });

    // Set image preview if editing announcement with image
    if (announcement.image_url) {
      setImagePreview(announcement.image_url);
    } else {
      setImagePreview(null);
    }

    setEditingId(announcement.id);
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) {
      return;
    }

    try {
      const { error } = await postgrest
        .from("announcements")
        .delete()
        .eq("id", id);

      if (error) throw error;
      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to delete announcement:", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
    setErrors({});
    setImagePreview(null);
  };

  const handleImageUpload = (base64Image: string) => {
    setFormData({ ...formData, image_url: base64Image });
    setImagePreview(base64Image);

    // Clear any previous errors
    const newErrors = { ...errors };
    delete newErrors.image_url;
    setErrors(newErrors);
  };

  const handlePreview = (announcement: Announcement) => {
    setPreviewAnnouncement(announcement);
  };

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "body",
      headerName: "Body",
      flex: 2,
      minWidth: 250,
      headerAlign: "left",
      align: "left",
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "media",
      headerName: "Media",
      width: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.video_url) {
          return <VideoLibraryIcon color="primary" titleAccess="Video" />;
        } else if (params.row.image_url) {
          return <ImageIcon color="primary" titleAccess="Image" />;
        }
        return (
          <Typography variant="caption" color="text.secondary">
            None
          </Typography>
        );
      },
    },
    {
      field: "has_link",
      headerName: "Link",
      width: 80,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.action_link) {
          return (
            <LinkIcon color="primary" titleAccess={params.row.action_link} />
          );
        }
        return null;
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        const expirationDate = new Date(params.row.expiration_date);
        const now = new Date();
        const isActive = expirationDate > now;

        return (
          <Chip
            label={isActive ? "Active" : "Expired"}
            color={isActive ? "success" : "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "expiration_date",
      headerName: "Expires",
      width: 180,
      headerAlign: "left",
      align: "left",
      renderCell: (params: GridRenderCellParams) =>
        new Date(params.value).toLocaleString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 280,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" onClick={() => handlePreview(params.row)}>
            Preview
          </Button>
          <Button size="small" onClick={() => handleEdit(params.row)}>
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            Add Announcement
          </Button>
        </Stack>

        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={announcements}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        </Box>
      </Stack>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? "Edit Announcement" : "New Announcement"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
            />

            <TextField
              label="Body"
              value={formData.body}
              onChange={(e) =>
                setFormData({ ...formData, body: e.target.value })
              }
              error={!!errors.body}
              helperText={errors.body}
              multiline
              rows={6}
              fullWidth
            />

            {/* Media Type Selector */}
            <FormControl fullWidth>
              <FormLabel>Media Type</FormLabel>
              <ToggleButtonGroup
                value={formData.media_type}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    setFormData({
                      ...formData,
                      media_type: value,
                      // Clear opposite media when switching
                      video_url: value === "video" ? formData.video_url : "",
                      image_url: value === "image" ? formData.image_url : "",
                    });
                    if (value !== "image") {
                      setImagePreview(null);
                    }
                  }
                }}
                fullWidth
                sx={{ mt: 1 }}
              >
                <ToggleButton value="none">None</ToggleButton>
                <ToggleButton value="video">
                  <VideoLibraryIcon sx={{ mr: 1 }} />
                  Video
                </ToggleButton>
                <ToggleButton value="image">
                  <ImageIcon sx={{ mr: 1 }} />
                  Image
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            {/* Video Upload Field */}
            {formData.media_type === "video" && (
              <TextField
                label="YouTube Embed Code"
                value={formData.video_url}
                onChange={(e) =>
                  setFormData({ ...formData, video_url: e.target.value })
                }
                error={!!errors.video_url}
                helperText={
                  errors.video_url || "Paste the iframe embed code from YouTube"
                }
                multiline
                rows={3}
                fullWidth
              />
            )}

            {/* Image Upload Field */}
            {formData.media_type === "image" && (
              <Box>
                {!imagePreview ? (
                  <ButtonUploadImage
                    label="Upload Image"
                    onLoad={handleImageUpload}
                  />
                ) : (
                  <Stack spacing={2}>
                    <Box sx={{ textAlign: "center" }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <ButtonUploadImage
                      label="Change Image"
                      onLoad={handleImageUpload}
                    />
                  </Stack>
                )}
                {errors.image_url && (
                  <FormHelperText error>{errors.image_url}</FormHelperText>
                )}
              </Box>
            )}

            {/* Action Link Fields */}
            <TextField
              label="Action Link (optional)"
              value={formData.action_link}
              onChange={(e) =>
                setFormData({ ...formData, action_link: e.target.value })
              }
              error={!!errors.action_link}
              helperText={
                errors.action_link ||
                "Enter a URL to add a clickable link to the announcement"
              }
              placeholder="https://example.com"
              fullWidth
            />

            {formData.action_link && (
              <TextField
                label="Link Button Text (optional)"
                value={formData.action_link_text}
                onChange={(e) =>
                  setFormData({ ...formData, action_link_text: e.target.value })
                }
                helperText="Default: 'Learn More'"
                placeholder="Learn More"
                fullWidth
              />
            )}

            <React.Suspense
              fallback={
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress />
                </Box>
              }
            >
              <DatePickerField
                label="Expiration Date"
                value={formData.expiration_date}
                onChange={(newValue) =>
                  setFormData({ ...formData, expiration_date: newValue })
                }
                error={!!errors.expiration_date}
                helperText={errors.expiration_date}
              />
            </React.Suspense>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? "Uploading..." : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {previewAnnouncement && (
        <Dialog
          open={!!previewAnnouncement}
          onClose={() => setPreviewAnnouncement(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{previewAnnouncement.title}</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography>{previewAnnouncement.body}</Typography>

              {previewAnnouncement.action_link && (
                <Button
                  variant="contained"
                  href={previewAnnouncement.action_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  sx={{
                    backgroundColor: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        transform: "scale(1)",
                        boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.7)",
                      },
                      "50%": {
                        transform: "scale(1.02)",
                        boxShadow: "0 0 20px 0 rgba(25, 118, 210, 0.4)",
                      },
                    },
                  }}
                >
                  {previewAnnouncement.action_link_text || "Learn More"}
                </Button>
              )}

              {previewAnnouncement.video_url && (
                <SafeIframe iframeHtml={previewAnnouncement.video_url} />
              )}

              {previewAnnouncement.image_url && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    my: 1,
                  }}
                >
                  <img
                    src={previewAnnouncement.image_url}
                    alt={previewAnnouncement.title}
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              )}

              <Typography variant="caption" color="text.secondary">
                Expires:{" "}
                {new Date(previewAnnouncement.expiration_date).toLocaleString()}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewAnnouncement(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
